import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  MagnifyingGlassIcon,
  LinkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  ClipboardIcon,
  ChartBarIcon,
  CalendarIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { adminApiService } from '../../../shared/services/adminApi'
import { SystemStatusCard, AdminHeader } from '../../../shared/components'
import toast from 'react-hot-toast'
import type { SystemHealth } from '../../../shared/services/adminApi'
import { formatDate as formatLocaleDate } from '../../../shared/lib/format'

interface LinkData {
  _id: string
  shortCode: string
  originalUrl: string
  customAlias?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  expiresAt?: string
  clicks: number
  lastClickAt?: string
  userId: {
    _id: string
    email: string
    name?: string
    plan: string
  }
  tags: string[]
  description?: string
  qrCode?: string
  isPasswordProtected: boolean
  isCustomDomain: boolean
  domain?: string
}

interface LinkStats {
  totalLinks: number
  activeLinks: number
  totalClicks: number
  linksToday: number
  clicksToday: number
  avgClicksPerLink: number
}

interface LinkDeletionPreview {
  link: {
    id: string
    shortCode: string
    originalUrl: string
    createdAt: string
    clicks: number
  }
  deletionPreview: {
    clicksToDelete: number
    analyticsToDelete: number
    dateRanges: {
      firstClick: string | null
      lastClick: string | null
    }
  }
  warning: string
}

export default function LinkManagementPage() {
  const [links, setLinks] = useState<LinkData[]>([])
  const [stats, setStats] = useState<LinkStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'active' | 'inactive'
  >('all')
  const [sortBy, setSortBy] = useState<'createdAt' | 'clicks' | 'lastClickAt'>(
    'createdAt'
  )
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedLinks, setSelectedLinks] = useState<string[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [linkToDelete, setLinkToDelete] = useState<string | null>(null)
  const [deletionPreview, setDeletionPreview] =
    useState<LinkDeletionPreview | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false)

  const loadLinks = async () => {
    try {
      setLoading(true)
      const data = await adminApiService.getLinks({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        sortBy,
        sortOrder,
      })
      setLinks(data.links)
      setTotalPages(data.pagination.totalPages)
      setStats(data.stats)
    } catch (error) {
      console.error('Failed to load links:', error)
      toast.error('Failed to load links')
    } finally {
      setLoading(false)
    }
  }

  const loadSystemHealth = async () => {
    try {
      const healthData = await adminApiService.getSystemHealth()
      setSystemHealth(healthData)
    } catch (error) {
      console.error('Failed to load system health:', error)
    }
  }

  const handleRefreshAll = async () => {
    try {
      setIsRefreshingStatus(true)
      await Promise.all([loadLinks(), loadSystemHealth()])
      toast.success('Data refreshed successfully')
    } catch (error) {
      console.error('Failed to refresh data:', error)
      toast.error('Failed to refresh data')
    } finally {
      setIsRefreshingStatus(false)
    }
  }

  useEffect(() => {
    loadLinks()
    loadSystemHealth()
  }, [page, search, statusFilter, sortBy, sortOrder])

  const handleStatusChange = async (linkId: string, isActive: boolean) => {
    try {
      await adminApiService.updateLinkStatus(linkId, isActive)
      await loadLinks()
      toast.success(
        `Link ${isActive ? 'activated' : 'deactivated'} successfully`
      )
    } catch (error) {
      console.error('Failed to update link status:', error)
      toast.error('Failed to update link status')
    }
  }

  const handleDeleteClick = async (linkId: string) => {
    try {
      const preview = await adminApiService.getLinkDeletionPreview(linkId)
      setDeletionPreview(preview)
      setLinkToDelete(linkId)
      setShowDeleteModal(true)
    } catch (error) {
      console.error('Failed to get deletion preview:', error)
      toast.error('Failed to get deletion preview')
    }
  }

  const handleConfirmDelete = async () => {
    if (!linkToDelete) {
      return
    }

    try {
      setIsDeleting(true)
      const result = await adminApiService.deleteLink(linkToDelete)

      toast.success(
        `Link deleted successfully. Removed ${result.deletedCounts.clicks} clicks and ${result.deletedCounts.analytics} analytics records.`
      )

      setShowDeleteModal(false)
      setLinkToDelete(null)
      setDeletionPreview(null)
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to delete link:', error)
      toast.error(error.response?.data?.message || 'Failed to delete link')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) {
      toast.error('Please select links to delete')
      return
    }

    if (
      !confirm(
        `Are you sure you want to delete ${selectedLinks.length} links? This action cannot be undone.`
      )
    ) {
      return
    }

    try {
      setIsDeleting(true)
      const result = await adminApiService.bulkDeleteLinks(selectedLinks)

      toast.success(
        `${result.deletedCounts.links} links deleted successfully. Removed ${result.deletedCounts.clicks} clicks and ${result.deletedCounts.analytics} analytics records.`
      )

      setSelectedLinks([])
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to bulk delete links:', error)
      toast.error(error.response?.data?.message || 'Failed to delete links')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleBulkStatusChange = async (isActive: boolean) => {
    if (selectedLinks.length === 0) {
      toast.error('Please select links to update')
      return
    }

    try {
      await adminApiService.bulkUpdateLinkStatus(selectedLinks, isActive)
      toast.success(
        `${selectedLinks.length} links ${isActive ? 'activated' : 'deactivated'} successfully`
      )
      setSelectedLinks([])
      await loadLinks()
    } catch (error: any) {
      console.error('Failed to bulk update link status:', error)
      toast.error(
        error.response?.data?.message || 'Failed to update link status'
      )
    }
  }

  const handleSelectLink = (linkId: string) => {
    setSelectedLinks(prev =>
      prev.includes(linkId)
        ? prev.filter(id => id !== linkId)
        : [...prev, linkId]
    )
  }

  const handleSelectAll = () => {
    if (selectedLinks.length === links.length) {
      setSelectedLinks([])
    } else {
      setSelectedLinks(links.map(link => link._id))
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard')
  }

  const formatDate = (dateString: string) => {
    return formatLocaleDate(dateString)
  }

  const formatUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) {
      return url
    }
    return url.substring(0, maxLength) + '...'
  }

  const getFullShortUrl = (link: LinkData) => {
    if (link.isCustomDomain && link.domain) {
      return `https://${link.domain}/${link.shortCode}`
    }
    return `https://shorty.kr/${link.shortCode}`
  }
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="Link Management"
        subtitle="Manage shortened URLs, analytics, and access controls"
        showRefresh={true}
        onRefresh={handleRefreshAll}
        isRefreshing={isRefreshingStatus}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <SystemStatusCard
          systemHealth={systemHealth}
          onRefresh={handleRefreshAll}
          isRefreshing={isRefreshingStatus}
          className="mb-6"
        />

        {/* Stats Cards */}
        {stats && (
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <LinkIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Links
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.totalLinks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                      <div className="h-2 w-2 rounded-full bg-green-600 dark:bg-green-400"></div>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Active Links
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.activeLinks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total Clicks
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.totalClicks.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CalendarIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Links Today
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.linksToday.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Clicks Today
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.clicksToday.toLocaleString()}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ChartBarIcon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                        Avg Clicks
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {stats.avgClicksPerLink.toFixed(1)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedLinks.length > 0 && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
            <div className="flex items-center justify-between">
              <span className="text-sm text-blue-800 dark:text-blue-200">
                {selectedLinks.length} link{selectedLinks.length > 1 ? 's' : ''}{' '}
                selected
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setSelectedLinks([])}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  Clear selection
                </button>
                <button
                  onClick={() => handleBulkStatusChange(true)}
                  className="inline-flex items-center rounded-md border border-transparent bg-green-600 px-3 py-1 text-sm font-medium text-white hover:bg-green-700"
                >
                  Activate Selected
                </button>
                <button
                  onClick={() => handleBulkStatusChange(false)}
                  className="inline-flex items-center rounded-md border border-transparent bg-orange-600 px-3 py-1 text-sm font-medium text-white hover:bg-orange-700"
                >
                  Deactivate Selected
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <TrashIcon className="mr-1 h-4 w-4" />
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Search links by short code, URL, or description..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 leading-5 text-gray-900 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-400"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={e => {
                const [field, order] = e.target.value.split('-')
                setSortBy(field as any)
                setSortOrder(order as any)
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
              <option value="createdAt-desc">Newest First</option>
              <option value="createdAt-asc">Oldest First</option>
              <option value="clicks-desc">Most Clicks</option>
              <option value="clicks-asc">Least Clicks</option>
              <option value="lastClickAt-desc">Recent Activity</option>
            </select>
          </div>
        </div>

        <div className="overflow-hidden bg-white shadow dark:bg-gray-800 sm:rounded-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Loading links...
              </p>
            </div>
          ) : links.length === 0 ? (
            <div className="p-8 text-center">
              <LinkIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No links found
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Try adjusting your search criteria or filters.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      <input
                        type="checkbox"
                        checked={
                          selectedLinks.length === links.length &&
                          links.length > 0
                        }
                        onChange={handleSelectAll}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Original URL
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Clicks
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {links.map((link, index) => (
                    <motion.tr
                      key={link._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedLinks.includes(link._id)}
                          onChange={() => handleSelectLink(link._id)}
                          className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <LinkIcon className="h-5 w-5 text-gray-400" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {link.customAlias || link.shortCode}
                            </div>
                            <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                              {formatUrl(getFullShortUrl(link), 30)}
                              <button
                                onClick={() =>
                                  copyToClipboard(getFullShortUrl(link))
                                }
                                className="ml-2 rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                                title="Copy link"
                              >
                                <ClipboardIcon className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {formatUrl(link.originalUrl, 40)}
                        </div>
                        {link.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatUrl(link.description, 50)}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-6 w-6 flex-shrink-0">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 dark:bg-gray-600">
                              <UserIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {link.userId?.name ||
                                link.userId?.email?.split('@')[0] ||
                                'Unknown User'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {link.userId?.plan || 'free'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {link.clicks.toLocaleString()}
                        </div>
                        {link.lastClickAt && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Last: {formatDate(link.lastClickAt)}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            link.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                          }`}
                        >
                          {link.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {link.isPasswordProtected && (
                          <div className="mt-1">
                            <span className="inline-flex items-center rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                              🔒 Protected
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(link.createdAt)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() =>
                              window.open(getFullShortUrl(link), '_blank')
                            }
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                            title="Visit link"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() =>
                              handleStatusChange(link._id, !link.isActive)
                            }
                            className={`text-sm font-medium ${
                              link.isActive
                                ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300'
                                : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                            }`}
                          >
                            {link.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(link._id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            title="Delete link"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Page {page} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletionPreview && (
          <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600 bg-opacity-50">
            <div className="relative top-20 mx-auto w-11/12 max-w-2xl rounded-md border bg-white p-5 shadow-lg dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex items-center">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4 text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                      Delete Link
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        You are about to permanently delete the link{' '}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {deletionPreview.link.shortCode}
                        </span>
                      </p>
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                        {deletionPreview.warning}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowDeleteModal(false)
                      setLinkToDelete(null)
                      setDeletionPreview(null)
                    }}
                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={isDeleting}
                    className="rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isDeleting ? 'Deleting...' : 'Delete Link'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
